<template>
  <div
    v-if="estimate"
    class="estimate-pdf-page"
    data-estimate-pdf
    data-estimate-pdf-ready="true"
  >
    <div v-if="isDraft" class="draft-watermark">DRAFT</div>

    <div class="document-meta">
      <div class="recipient-block">
        <div class="recipient-label">To:</div>
        <div class="recipient-name">{{ recipientName }}</div>
        <div v-if="estimate.recipient?.postal_code">
          {{ estimate.recipient.postal_code }}
        </div>
        <div>{{ estimate.recipient?.address || '' }}</div>
      </div>
      <div class="issuer-block">
        <div>{{ formatEnglishDate(estimate.issue_date) }}</div>
        <div>Quotation No. {{ estimate.estimate_no }}</div>
        <div v-if="Number(estimate.revision_no) > 0">
          {{ formatRevisionLabel(estimate.revision_no) }}
        </div>
      </div>
    </div>

    <h1>QUOTATION</h1>

    <div class="intro-row">
      <p>We are pleased to submit the following quotation.</p>
      <div
        :class="[
          'company-block',
          { 'company-block--with-seal': Boolean(estimate.seal_flg) }
        ]"
      >
        <div class="company-name">Soei Planning Co., Ltd.</div>
        <div>2-38-6 Yoyogi, Shibuya-ku,</div>
        <div>Tokyo 151-0053, Japan</div>
        <div>Tel: 03-3370-8006 / Fax: 03-3370-8157</div>
        <div>Registration No. T7011001012662</div>
        <div v-if="estimate.created_by_user?.name">
          Contact: {{ estimate.created_by_user.name }}
        </div>
        <CompanySeal :visible="Boolean(estimate.seal_flg)" class="company-seal" />
      </div>
    </div>

    <div class="total-box">
      <span>Total Quotation Amount</span>
      <strong>{{ currency(estimate.total_amount) }}</strong>
      <small>(incl. consumption tax)</small>
    </div>

    <table class="condition-table">
      <tbody>
        <tr>
          <th>Subject</th>
          <td>{{ estimate.title }}</td>
        </tr>
        <tr>
          <th>Work Location</th>
          <td>{{ workLocation }}</td>
        </tr>
        <tr>
          <th>Valid Until</th>
          <td>{{ estimate.valid_until ? formatEnglishDate(estimate.valid_until) : '-' }}</td>
        </tr>
      </tbody>
    </table>

    <table class="detail-table">
      <thead>
        <tr>
          <th class="item-column">Description</th>
          <th class="spec-column">Details</th>
          <th class="quantity-column">Qty.</th>
          <th class="unit-column">Unit</th>
          <th class="price-column">Unit Price</th>
          <th class="amount-column">Amount</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="row in displayRows" :key="row.key">
          <tr v-if="row.kind === 'section'" class="section-row">
            <td colspan="6">{{ row.name }}</td>
          </tr>
          <tr v-else :class="[`line-${row.line.line_type}`, 'estimate-detail-row']">
            <td>{{ lineName(row.line) }}</td>
            <td class="multiline">{{ lineSpecification(row.line) }}</td>
            <td class="number-cell">{{ quantity(row.line) }}</td>
            <td class="center-cell">
              {{ row.line.unit_nm }}
            </td>
            <td class="number-cell">{{ unitPrice(row.line) }}</td>
            <td class="number-cell">{{ lineAmount(row.line) }}</td>
          </tr>
        </template>
        <tr v-if="displayRows.length === 0">
          <td colspan="6" class="empty-row">No items.</td>
        </tr>
      </tbody>
    </table>

    <div class="summary-wrapper">
      <table class="summary-table">
        <tbody>
          <tr>
            <th>Subtotal</th>
            <td>{{ currency(estimate.subtotal_amount) }}</td>
          </tr>
          <tr v-if="Number(estimate.discount_amount || 0) !== 0">
            <th>Discount</th>
            <td>-{{ currency(estimate.discount_amount) }}</td>
          </tr>
          <tr>
            <th>Consumption Tax</th>
            <td>{{ currency(estimate.tax_amount) }}</td>
          </tr>
          <tr class="grand-total-row">
            <th>Total</th>
            <td>{{ currency(estimate.total_amount) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="generalNotes"
      class="notes-block"
    >
      <div v-if="generalNotes">
        <strong>Notes:</strong>
        <span>{{ generalNotes }}</span>
      </div>
    </div>

    <div v-if="isDraft" class="draft-note">
      This quotation is a draft pending internal approval.
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import CompanySeal from '@/components/shared/CompanySeal.vue';
import { formatEstimateQuantity } from '@/utils/estimateEditor';
import { formatRevisionLabel } from '@/utils/estimates';
import { formatEnglishGregorianDate } from '@/utils/estimatePdfDates';

const props = defineProps({
  estimate: {
    type: Object,
    required: true
  }
});

const isDraft = computed(() => props.estimate?.approval_status !== 'approved');

const recipientName = computed(() => {
  const recipient = props.estimate?.recipient || {};
  return [recipient.customer_nm, recipient.department_nm]
    .filter(Boolean)
    .join(', ');
});

const workLocation = computed(() =>
  props.estimate?.work_location ||
  [
    props.estimate?.building_snapshot?.building_nm,
    props.estimate?.building_snapshot?.address
  ].filter(Boolean).join(', ') ||
  '-'
);

const generalNotes = computed(() => props.estimate?.general_notes || '');

const displayRows = computed(() => {
  const rows = [];
  (props.estimate?.sections || []).forEach(section => {
    rows.push({
      key: `section-${section.id}`,
      kind: 'section',
      name: section.section_nm
    });
    (section.lines || []).forEach(line => {
      rows.push({
        key: `line-${line.id || `${section.id}-${line.sort_no}`}`,
        kind: 'line',
        line
      });
    });
  });
  (props.estimate?.unsectioned_lines || []).forEach(line => {
    rows.push({
      key: `unsectioned-line-${line.id || line.sort_no}`,
      kind: 'line',
      line
    });
  });
  return rows;
});

const currency = value =>
  `¥${Math.round(Number(value || 0)).toLocaleString('en-US')}`;

const formatEnglishDate = formatEnglishGregorianDate;

const lineName = line => {
  if (line.line_type === 'subtotal') {
    return line.item_nm || 'Subtotal';
  }
  if (line.line_type === 'discount') {
    return line.item_nm || 'Discount';
  }
  if (line.line_type === 'note') {
    return line.item_nm || 'Note';
  }
  return line.item_nm;
};

const lineSpecification = line =>
  [
    line.part_nm,
    line.specification,
    line.notes
  ].filter(Boolean).join('\n');

const quantity = line => {
  if (['note', 'subtotal'].includes(line.line_type) || line.quantity == null) return '';
  return formatEstimateQuantity(line.quantity, 'en-US', line.quantity_decimal_places);
};

const unitPrice = line => {
  if (['note', 'subtotal'].includes(line.line_type) || line.unit_price == null) return '';
  return Math.round(Number(line.unit_price)).toLocaleString('en-US');
};

const lineAmount = line => {
  if (line.line_type === 'note') return '';
  const amount = Math.round(Number(line.amount || 0)).toLocaleString('en-US');
  return line.line_type === 'discount' ? `-${amount}` : amount;
};
</script>

<style scoped>
.estimate-pdf-page {
  --pdf-rule: 1px solid #666;
  --pdf-rule-dark: 1px solid #555;
  position: relative;
  box-sizing: border-box;
  width: 210mm;
  min-height: 297mm;
  padding: 11mm 13mm 10mm;
  overflow: hidden;
  color: #111;
  background: #fff;
  font-family: "Times New Roman", Times, serif;
  font-size: 10pt;
  line-height: 1.4;
}

.draft-watermark {
  position: absolute;
  top: 122mm;
  left: 23mm;
  z-index: 0;
  color: rgba(180, 0, 0, 0.08);
  font-family: Arial, sans-serif;
  font-size: 68pt;
  font-weight: 700;
  letter-spacing: 8mm;
  transform: rotate(-28deg);
  pointer-events: none;
}

.estimate-pdf-page > *:not(.draft-watermark) {
  position: relative;
  z-index: 1;
}

.document-meta {
  display: flex;
  min-height: 30mm;
  justify-content: space-between;
  gap: 10mm;
}

.recipient-block {
  width: 105mm;
}

.recipient-label {
  font-weight: 700;
}

.recipient-name {
  margin-bottom: 2mm;
  padding-bottom: 1mm;
  border-bottom: var(--pdf-rule);
  font-size: 14pt;
}

.issuer-block {
  width: 65mm;
  text-align: right;
}

h1 {
  margin: 1mm 0 6mm;
  font-size: 24pt;
  font-weight: 500;
  letter-spacing: 2mm;
  text-align: center;
}

.intro-row {
  display: flex;
  min-height: 30mm;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8mm;
}

.intro-row > p {
  margin: 4mm 0 0;
  font-size: 11pt;
}

.company-block {
  position: relative;
  box-sizing: border-box;
  min-width: 82mm;
  font-size: 8.5pt;
  text-align: right;
}

.company-block--with-seal {
  padding-top: 17.5mm;
}

.company-seal {
  position: absolute;
  top: 0;
  right: 16mm;
}

.company-name {
  margin-bottom: 1mm;
  font-size: 13pt;
  font-weight: 700;
}

.total-box {
  display: flex;
  margin: 3mm 0 4mm;
  padding: 2.5mm 4mm;
  align-items: baseline;
  border: var(--pdf-rule);
  gap: 4mm;
}

.total-box span {
  font-size: 11pt;
}

.total-box strong {
  flex: 1;
  border-bottom: var(--pdf-rule);
  font-size: 18pt;
  font-weight: 400;
  text-align: right;
}

.total-box small {
  font-size: 8pt;
  white-space: nowrap;
}

table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.condition-table {
  margin-bottom: 4mm;
}

.condition-table,
.detail-table,
.summary-table {
  border-top: var(--pdf-rule);
  border-left: var(--pdf-rule);
}

.condition-table th,
.condition-table td {
  padding: 1.2mm 2mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
}

.condition-table th {
  width: 31mm;
  background: #f2f2f2;
  font-weight: 400;
}

.detail-table th,
.detail-table td {
  height: 7mm;
  padding: 1mm 1.4mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
  vertical-align: top;
}

.detail-table thead th {
  background: #ececec;
  font-weight: 400;
  text-align: center;
}

.item-column {
  width: 40mm;
}

.spec-column {
  width: 67mm;
}

.quantity-column {
  width: 14mm;
}

.unit-column {
  width: 13mm;
}

.price-column,
.amount-column {
  width: 23mm;
}

.section-row td {
  height: auto;
  padding: 1.3mm 2mm;
  background: #e5e5e5;
  font-weight: 400;
}

.line-note td {
  color: #333;
  font-size: 8.5pt;
}

.line-subtotal td,
.line-discount td {
  font-weight: 400;
}

.multiline,
.notes-block span {
  white-space: pre-line;
}

.number-cell {
  white-space: nowrap;
  text-align: right;
}

.center-cell,
.empty-row {
  text-align: center;
}

.summary-wrapper {
  display: flex;
  justify-content: flex-end;
}

.summary-table {
  width: 74mm;
}

.summary-table th,
.summary-table td {
  padding: 1.2mm 2mm;
  border: 0;
  border-right: var(--pdf-rule);
  border-bottom: var(--pdf-rule);
}

.summary-table th {
  width: 31mm;
  background: #f2f2f2;
  font-weight: 400;
  text-align: right;
}

.summary-table td {
  text-align: right;
}

.grand-total-row th,
.grand-total-row td {
  font-size: 11pt;
  font-weight: 400;
}

.notes-block {
  margin-top: 4mm;
  padding: 2mm 3mm;
  border: var(--pdf-rule-dark);
  font-size: 8.5pt;
}

.notes-block strong {
  margin-right: 1.5mm;
  font-weight: 400;
}

.notes-block > div + div {
  margin-top: 1mm;
}

.draft-note {
  margin-top: 2mm;
  color: #a00;
  font-family: Arial, sans-serif;
  font-size: 8pt;
  text-align: right;
}
</style>
